import { useEffect, useState } from 'react'; // Import useState for pagination
import { css } from '@emotion/react';
import {
  DashboardWidget,
  DashboardWidgetFC,
  TimePeriodListBoxMenu,
  useTimePeriodListBoxMenuState,
} from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { ContentLoader, Text } from '@frontend/design-system';
import { useSchedulePulseInsuranceAppointmentStats } from '../hooks/schedule-pulse-hooks/use-schedule-pulse-insurance-appointment-stats';

/**
 * @dashboard-widget
 *
 * id: verify-insurance-widget
 * title: Verify Insurance
 * description: Review and manually verify patients with unverified or error insurance statuses.
 * icon: shield
 */
export const VerifyInsuranceWidget: DashboardWidgetFC = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const { t } = useTranslation();
  const { startDate, endDate, ...timePeriodMenuState } = useTimePeriodListBoxMenuState();
  const { isLoading, appointmentsData } = useSchedulePulseInsuranceAppointmentStats({
    widgetStartDate: startDate,
    widgetEndDate: endDate,
    locationIds: selectedLocationIds,
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const ITEMS_PER_PAGE = 3;
  const totalAlerts = appointmentsData.length || 0;

  useEffect(() => {
    // Reset to the first page whenever the date range or location changes
    setCurrentIndex(0);
  }, [startDate, endDate, selectedLocationIds]);

  const handleNext = () => {
    // Move to the next page if there are more items to show
    if (currentIndex + ITEMS_PER_PAGE < totalAlerts) {
      setCurrentIndex(currentIndex + ITEMS_PER_PAGE);
    }
  };

  const handlePrev = () => {
    // Move to the previous page, but not before the start
    setCurrentIndex((prevIndex) => Math.max(0, prevIndex - ITEMS_PER_PAGE));
  };

  // Determining if caret buttons should be disabled ---
  const isPrevDisabled = currentIndex === 0;
  const isNextDisabled = currentIndex + ITEMS_PER_PAGE >= totalAlerts;

  // Prepare the list of appointments to display for the current page
  const displayedAppointments = appointmentsData?.slice(currentIndex, currentIndex + ITEMS_PER_PAGE) || [];

  return (
    <DashboardWidget>
      <DashboardWidget.Header>
        <TimePeriodListBoxMenu {...timePeriodMenuState} />
      </DashboardWidget.Header>

      <DashboardWidget.Content css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
        <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Text
              css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5), fontSize: '1.5rem' }}
              weight='bold'
              size='large'
            >
              {totalAlerts}{' '}
            </Text>
            <Text weight='light' size='large'>
              {t('Alerts')}
            </Text>
          </div>
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Icon name='caret-left' onClick={handlePrev} css={caretIconStyles(isPrevDisabled)} />
            <Icon name='caret-right' onClick={handleNext} css={caretIconStyles(isNextDisabled)} />
          </div>
        </div>
        <div
          css={{
            backgroundColor: theme.colors.neutral5,
            gap: theme.spacing(1),
            display: 'flex',
            flexDirection: 'column',
            padding: `${theme.spacing(1)} ${theme.spacing(1)} 0 ${theme.spacing(1)}`,
            borderRadius: theme.borderRadius.small,
            height: '176px',
          }}
        >
          {isLoading && <ContentLoader show />}
          {!isLoading &&
            displayedAppointments.length > 0 &&
            displayedAppointments.map((appt) => (
              <PatientInfo
                key={appt.id}
                status={appt.insuranceStatus}
                patientName={appt.name}
                timeRange={appt.appointmentTime}
              />
            ))}
          {!isLoading && totalAlerts === 0 && (
            <Text
              css={{ margin: 'auto', width: theme.spacing(25), color: theme.colors.neutral50, textAlign: 'center' }}
              weight='light'
            >
              {t('No insurance alerts found for this time period')}
            </Text>
          )}
        </div>
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

VerifyInsuranceWidget.config = {
  size: 'medium-narrow',
  feature: 'insurance',
};

interface PatientInfoProps {
  status: string;
  patientName: string;
  timeRange: string;
}

export const PatientInfo = ({ status, patientName, timeRange }: PatientInfoProps) => {
  const isErrorStatus = status === 'Error';
  const config = {
    badgeColor: isErrorStatus ? theme.colors.critical[30] : theme.colors.warning[10],
    insuranceIconBGColor: isErrorStatus ? theme.colors.critical[10] : theme.colors.warning[5],
  };

  return (
    <div css={containerStyles(config.badgeColor)}>
      <div css={{ display: 'flex', flexDirection: 'column' }}>
        <Text weight='bold' css={{ lineHeight: 1 }}>
          {patientName}
        </Text>
        <Text weight='light'>{timeRange}</Text>
      </div>
      <div css={badgeStyles(config.insuranceIconBGColor)}>
        {isErrorStatus ? (
          <Icon name='insurance-error-small' css={{ color: theme.colors.critical60 }} />
        ) : (
          <Icon name='insurance-incomplete-small' css={{ color: theme.colors.warning[70] }} />
        )}
      </div>
    </div>
  );
};

const containerStyles = (badgeColor: string) => css`
  border-radius: ${theme.borderRadius.small};
  padding: ${theme.spacing(0.5)} ${theme.spacing(2)} ${theme.spacing(0.5)} ${theme.spacing(2)};
  box-shadow: ${theme.shadows.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  background-color: ${theme.colors.white};
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${badgeColor};
    border-radius: ${theme.borderRadius.medium} 0 0 ${theme.borderRadius.medium};
    width: 4px;
    height: 100%;
  }
`;

const badgeStyles = (bgColor: string) => css`
  background-color: ${bgColor};
  padding: ${theme.spacing(1)} 12px;
  border-radius: ${theme.borderRadius.small};
`;

const caretIconStyles = (isDisabled: boolean) => css`
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  opacity: ${isDisabled ? 0.4 : 1};
  color: ${theme.colors.icon.disabled};
`;
